/*
 * Classes page. Opens on the shopper's saved store and lets them look at any
 * other one.
 *
 * The saved store comes from store-search.js — the same localStorage key the
 * header dropdown, the locator and the PDP pickup badge all write. Choosing a
 * store anywhere on the site is what makes this page open on the right
 * schedule; this file reads that choice and never invents its own.
 *
 * The <select> is server-rendered from the metaobject, so it already lists
 * every store that has a calendar, with the ids on each option. That means no
 * directory fetch is needed for the common path — picking from the dropdown
 * works immediately. The directory is only loaded to resolve a saved store
 * whose id is not in the list, which cannot normally happen.
 *
 * Changing the dropdown deliberately does NOT change the saved store. Looking
 * at what another shop is running is not the same as moving your pickup
 * location, and silently repointing the PDP badge because someone browsed a
 * class list would be a nasty surprise.
 */
class ClassesFinder extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-classes-select]');
    this.result = this.querySelector('[data-classes-result]');
    this.owner = this.dataset.acuityOwner;

    if (!this.select || !this.result || !this.owner) return;

    this.select.addEventListener('change', () => this.showSelected());

    const saved = window.TandyStoreSearch && window.TandyStoreSearch.getSelected();
    if (saved && saved.store_id) this.preselect(saved.store_id);

    /* Follow the saved store when it changes while this page is open — picked
       in the header selector, most likely. Saving a store is a deliberate "this
       is my store", so the schedule follows it even if the shopper had been
       browsing another store's classes here. */
    window.addEventListener('store:selected', event => {
      const store = event.detail && event.detail.store;
      if (store && store.store_id) this.preselect(store.store_id);
    });
  }

  /* Point the dropdown at the saved store, if that store runs classes. */
  preselect(storeId) {
    const wanted = String(storeId).toUpperCase();
    const option = Array.prototype.find.call(
      this.select.options,
      opt => opt.value.toUpperCase() === wanted
    );

    if (option) {
      this.select.value = option.value;
      this.showSelected();
      return;
    }

    // The saved store exists but has no calendar, so it is not in the list.
    // Say so by name rather than showing the generic "choose a store" prompt —
    // otherwise it looks like the page simply forgot which store is theirs.
    // The dropdown goes back to its prompt: left on the previous store it
    // would name one store above a message about another.
    this.select.value = '';
    this.renderNoClasses();
  }

  showSelected() {
    const option = this.select.selectedOptions[0];
    if (!option || !option.value) {
      this.result.innerHTML =
        `<p class="classes-finder__message">${ClassesFinder.escape(this.dataset.emptyTitle)}</p>`;
      return;
    }

    const calendar = option.getAttribute('data-calendar');
    if (!calendar) {
      this.renderNoClasses();
      return;
    }

    // The place name ("Denver, CO"); the option text also carries the store number.
    const name = option.getAttribute('data-name') || option.textContent.trim();
    const url = option.getAttribute('data-url');
    const workshop = option.getAttribute('data-workshop');

    /* Both schedules go in one [data-schedules] container so schedule-tabs.js
       can turn them into tabs. It matters more here than on a store page: a
       shopper who came to this page to browse workshops would otherwise land on
       six screens of classes with no sign the workshop exists below them. With
       one schedule the container does nothing and this renders as it always
       has. */
    let html = `<div class="classes-finder__schedules" data-schedules`
      + ` data-schedules-label="${ClassesFinder.escape(name)}">`
      + `<section data-schedule="classes" data-schedule-label="${ClassesFinder.escape(this.dataset.classesTabLabel)}">`
      + `<h2 class="classes-finder__subtitle">${ClassesFinder.escape(name)}</h2>`
      + this.frame(calendar, `${name} class schedule`)
      + `</section>`;

    if (workshop) {
      html += `<section data-schedule="workshop" data-schedule-label="${ClassesFinder.escape(this.dataset.workshopTabLabel)}">`
        + `<h2 class="classes-finder__subtitle">${ClassesFinder.escape(this.dataset.workshopHeading)}</h2>`
        + this.frame(workshop, `${name} workshop booking`)
        + `</section>`;
    }

    html += `</div>`;

    if (url) {
      html += `<p class="classes-finder__store-link">
        <a href="${ClassesFinder.escape(url)}">Everything else about ${ClassesFinder.escape(name)}</a>
      </p>`;
    }

    this.result.innerHTML = html;
    if (window.TandyScheduleTabs) window.TandyScheduleTabs.initAll(this.result);
    this.loadAcuityScript();
  }

  frame(calendarId, title) {
    const src = 'https://app.acuityscheduling.com/schedule.php'
      + `?owner=${encodeURIComponent(this.owner)}`
      + `&calendarID=${encodeURIComponent(calendarId)}`;
    /* height as an ATTRIBUTE, with no height in the stylesheet: it is the
       starting size and the fallback, and acuity-frame.js overwrites it with an
       inline height once the schedule reports one. */
    return `<iframe class="classes-finder__frame"
      src="${src}"
      title="${ClassesFinder.escape(title)}"
      height="800"
      loading="lazy"
      frameborder="0"></iframe>`;
  }

  renderNoClasses() {
    this.result.innerHTML =
      `<p class="classes-finder__message">${ClassesFinder.escape(this.dataset.noneTitle)}</p>`
      + `<p class="classes-finder__message">${ClassesFinder.escape(this.dataset.noneBody)}</p>`;
  }

  /* Acuity's embed script, for the scroll-into-view it does as a shopper moves
     through the booking steps. NOT for sizing — it sweeps for frames once when
     it runs, so a frame this class creates later is never adopted, which is why
     acuity-frame.js listens for the resize message instead. Added once and
     never removed. */
  loadAcuityScript() {
    if (this.acuityLoaded) return;
    this.acuityLoaded = true;
    const tag = document.createElement('script');
    tag.src = 'https://embed.acuityscheduling.com/js/embed.js';
    tag.defer = true;
    document.body.appendChild(tag);
  }

  static escape(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, ch => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));
  }
}

!customElements.get('classes-finder') && customElements.define('classes-finder', ClassesFinder);

